import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useSceneStore } from '@/stores/scene-store';
import { Plus, X, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface Device3DManagerProps {
  sceneId: string;
}

export function Device3DManager({ sceneId }: Device3DManagerProps) {
  const {
    currentScene,
    addDevicePlacement,
    updateDevicePlacement,
    deleteDevicePlacement,
    addSensorVisual,
    updateSensorVisual,
    deleteSensorVisual,
  } = useSceneStore();

  const [showAddDevice, setShowAddDevice] = useState(false);
  const [newDeviceId, setNewDeviceId] = useState('');
  const [newDeviceName, setNewDeviceName] = useState('');
  const [selectedPlacement, setSelectedPlacement] = useState<string | null>(null);
  const [showAddSensor, setShowAddSensor] = useState(false);

  if (!currentScene) return null;

  const handleAddDevice = () => {
    if (!newDeviceId.trim()) {
      toast.error('Please enter a device ID');
      return;
    }

    addDevicePlacement(currentScene.id, {
      deviceId: newDeviceId,
      deviceName: newDeviceName || newDeviceId,
      position: { x: 0, y: 1, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
      sensors: [],
    });

    setNewDeviceId('');
    setNewDeviceName('');
    setShowAddDevice(false);
    toast.success('Device added to scene');
  };

  const handleDeleteDevice = (placementId: string) => {
    deleteDevicePlacement(currentScene.id, placementId);
    if (selectedPlacement === placementId) {
      setSelectedPlacement(null);
    }
    toast.success('Device removed from scene');
  };

  const handleAddSensor = (placementId: string, sensorType: 'gauge' | 'chart' | 'text' | 'heatmap' | 'billboard') => {
    addSensorVisual(currentScene.id, placementId, {
      sensorId: `sensor_${Date.now()}`,
      sensorName: `${sensorType} Sensor`,
      sensorType: 'temperature',
      type: sensorType,
      position: { x: 0, y: 0.5, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
      size: { width: 1, height: 1 },
      config: {
        showValue: true,
        showUnit: true,
        showLabel: true,
        color: '#3b82f6',
        alwaysFaceCamera: sensorType === 'text' || sensorType === 'billboard',
      },
      currentValue: Math.random() * 100,
      unit: '°C',
    });
    toast.success('Sensor visualization added');
  };

  const handleDeleteSensor = (placementId: string, sensorId: string) => {
    deleteSensorVisual(currentScene.id, placementId, sensorId);
    toast.success('Sensor visualization removed');
  };

  return (
    <div className="space-y-4">
      {/* Add Device Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center justify-between">
            Device Placements
            <Button size="sm" variant="outline" onClick={() => setShowAddDevice(!showAddDevice)} className="h-6 px-2">
              <Plus className="h-3 w-3" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {showAddDevice && (
            <div className="space-y-3 p-3 bg-gray-50 rounded-lg">
              <div>
                <Label className="text-xs">Device ID</Label>
                <Input
                  value={newDeviceId}
                  onChange={(e) => setNewDeviceId(e.target.value)}
                  placeholder="Enter device ID"
                  className="h-8"
                />
              </div>
              <div>
                <Label className="text-xs">Device Name (Optional)</Label>
                <Input
                  value={newDeviceName}
                  onChange={(e) => setNewDeviceName(e.target.value)}
                  placeholder="Enter device name"
                  className="h-8"
                />
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleAddDevice} className="h-7">
                  Add Device
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowAddDevice(false)} className="h-7">
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {/* Device List */}
          <div className="space-y-2">
            {currentScene.devicePlacements.map((placement) => (
              <DevicePlacementCard
                key={placement.id}
                placement={placement}
                isSelected={selectedPlacement === placement.id}
                onSelect={() => setSelectedPlacement(placement.id)}
                onDelete={() => handleDeleteDevice(placement.id)}
                onAddSensor={(type) => handleAddSensor(placement.id, type)}
                onDeleteSensor={(sensorId) => handleDeleteSensor(placement.id, sensorId)}
              />
            ))}

            {currentScene.devicePlacements.length === 0 && (
              <div className="text-center text-gray-500 py-6">
                <p className="text-sm">No devices placed</p>
                <p className="text-xs">Add devices to visualize sensor data</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

interface DevicePlacementCardProps {
  placement: any;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onAddSensor: (type: 'gauge' | 'chart' | 'text' | 'heatmap' | 'billboard') => void;
  onDeleteSensor: (sensorId: string) => void;
}

function DevicePlacementCard({
  placement,
  isSelected,
  onSelect,
  onDelete,
  onAddSensor,
  onDeleteSensor,
}: DevicePlacementCardProps) {
  const [showSensorMenu, setShowSensorMenu] = useState(false);

  return (
    <Card className={`cursor-pointer transition-colors ${isSelected ? 'ring-2 ring-blue-500' : ''}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between" onClick={onSelect}>
          <div>
            <CardTitle className="text-sm">{placement.deviceName}</CardTitle>
            <CardDescription className="text-xs">ID: {placement.deviceId}</CardDescription>
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                setShowSensorMenu(!showSensorMenu);
              }}
              className="h-6 w-6 p-0"
            >
              <Plus className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Sensor Menu */}
        {showSensorMenu && (
          <div className="mb-3 p-2 bg-gray-50 rounded">
            <Label className="text-xs font-medium mb-2 block">Add Sensor Visualization</Label>
            <div className="grid grid-cols-2 gap-1">
              {(['gauge', 'chart', 'text', 'billboard'] as const).map((type) => (
                <Button
                  key={type}
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onAddSensor(type);
                    setShowSensorMenu(false);
                  }}
                  className="h-7 text-xs capitalize"
                >
                  {type}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Sensor List */}
        <div className="space-y-2">
          <Label className="text-xs font-medium">Sensors ({placement.sensors.length})</Label>
          {placement.sensors.map((sensor: any) => (
            <div key={sensor.id} className="flex items-center justify-between p-2 bg-white rounded border">
              <div>
                <div className="text-xs font-medium">{sensor.sensorName}</div>
                <div className="text-xs text-gray-500">
                  {sensor.type} • {sensor.sensorType}
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onDeleteSensor(sensor.id)}
                className="h-6 w-6 p-0 text-red-500"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}

          {placement.sensors.length === 0 && (
            <div className="text-xs text-gray-500 text-center py-2">No sensor visualizations</div>
          )}
        </div>

        {/* Device Position Info */}
        <Separator className="my-2" />
        <div className="text-xs text-gray-500">
          Position: ({placement.position.x.toFixed(1)}, {placement.position.y.toFixed(1)},{' '}
          {placement.position.z.toFixed(1)})
        </div>
      </CardContent>
    </Card>
  );
}
